import React, { useRef, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, Platform, Image } from 'react-native';
import { useAuth } from '../hooks/useAuth';
import styles from '../../styles/AppStyles';

import { toast } from 'react-toastify';


// Map of Firebase Auth error codes → user-friendly messages
const firebaseErrorMessages: Record<string, string> = {
  'auth/invalid-email':         'Please enter a valid email address.',
  'auth/missing-password':      'Please enter your password.',
  'auth/user-disabled':         'This account has been disabled. Please contact support.',
  'auth/user-not-found':        'No account found with that email. Would you like to register?',
  'auth/wrong-password':        'Oops—that password does not match. Try again?',
  'auth/email-already-in-use':  'Looks like you already have an account with this email.',
  'auth/weak-password':         'Your password is too weak. Try at least 6 characters.',
  'auth/too-many-requests':     'Too many attempts. Please wait a moment before trying again.',
};

// Pulls out all “(auth/…)” codes and returns the first friendly match.
function getFirebaseFriendlyError(err: any): string {
  if (!err) return '';
  if (typeof err === 'string') return err;
  if (err.code && firebaseErrorMessages[err.code]) {
    return firebaseErrorMessages[err.code];
  }
  const msg: string = err.message || '';
  const matches = Array.from(msg.matchAll(/\((auth\/[^\)]+)\)/g), m => m[1]);
  for (const code of matches) {
    if (firebaseErrorMessages[code]) {
      return firebaseErrorMessages[code];
    }
  }
  return 'Something went wrong—please try again in a moment.';
}

export default function AuthScreen() {
  const { signUp, signIn, sendPasswordReset, loading, error: authError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetLoading, setResetLoading] = useState(false);

  const passwordInputRef = useRef<TextInput>(null);
 
  const LEMON_SQUEEZY_SIGNUP_URL =
    process.env.LEMON_SQUEEZY_SIGNUP_URL ||
    'https://vistaventures.lemonsqueezy.com/checkout';

  const handleSubmit = async () => {
    if (loading) return;
    setError(null);

    if (isForgotPassword) {
      setResetLoading(true);
      try {
        await sendPasswordReset(email);
        setResetSent(true);
      } catch (e: any) {
        setError(getFirebaseFriendlyError(e));
      } finally {
        setResetLoading(false);
      }
      return;
    }

    try {
      if (isRegister) {
        await signUp(email, password);
        if (Platform.OS === 'web') {
          window.location.href = LEMON_SQUEEZY_SIGNUP_URL;
        } else {
          const { Linking } = require('react-native');
          await Linking.openURL(LEMON_SQUEEZY_SIGNUP_URL);
        }
      } else {
        await signIn(email, password);
      }
    } catch (e: any) {
      setError(getFirebaseFriendlyError(e));
    }
  };

  const displayMessage =
    error
      ? error
      : !isForgotPassword && authError
        ? getFirebaseFriendlyError(authError)
        : null;

  return (
    <View style={styles.authContainer}>
      <View style={styles.authWrapper}>
        <View style={styles.authLogoPane}>
          <Image
            source={require('../../assets/LandingatorLogo.png')}
            style={{ width: undefined, height: '100%', resizeMode: 'contain' }}
          />
        </View>
        <View style={styles.authFormPane}>
          <View style={styles.authCard}>
            <Text style={styles.authTitle}>
              {isForgotPassword
                ? 'Recover Password'
                : isRegister
                  ? 'Create an Account'
                  : 'Sign In'}
            </Text>
            {displayMessage && (
              <Text style={styles.authError}>{displayMessage}</Text>
            )}
            {isForgotPassword ? (
              resetSent ? (
                <>
                  <Text style={[styles.authToggle, { color: '#fff', fontWeight: '600', fontSize: 15, marginBottom: 6, textAlign: 'center' }]}>
                    A password reset link has been sent to your email address.
                  </Text>
                  <Text style={[styles.authToggle, { color: 'rgba(255,255,255,0.7)', marginBottom: 16 }]}>
                    Don't see it? Check your spam or junk folder.
                  </Text>
                  <TouchableOpacity
                    onPress={() => {
                      setIsForgotPassword(false);
                      setResetSent(false);
                      setError(null);
                    }}
                    disabled={loading}
                  >
                    <Text style={styles.authToggle}>Back to Sign In</Text>
                  </TouchableOpacity>
                </>
              ) : (
                <>
                  <TextInput
                    style={styles.authInput}
                    placeholder="Email"
                    value={email}
                    onChangeText={setEmail}
                    editable={!loading}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    returnKeyType="go"
                    onSubmitEditing={handleSubmit}
                  />
                  <TouchableOpacity
                    style={[
                      styles.button,
                      styles.primaryButton,
                      resetLoading && styles.removeBtnDisabled,
                    ]}
                    onPress={handleSubmit}
                    disabled={resetLoading}
                  >
                    <Text style={styles.primaryButtonText}>
                      {resetLoading ? 'Sending…' : 'Send Reset Link'}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => {
                      setIsForgotPassword(false);
                      setError(null);
                    }}
                    disabled={resetLoading}
                  >
                    <Text style={styles.authToggle}>Back to Sign In</Text>
                  </TouchableOpacity>
                </>
              )
            ) : (
              <>
                <TextInput
                  style={styles.authInput}
                  placeholder="Email"
                  value={email}
                  onChangeText={setEmail}
                  editable={!loading}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  returnKeyType="next"
                  blurOnSubmit={false}
                  onSubmitEditing={() => passwordInputRef.current?.focus()}
                />
                <TextInput
                  ref={passwordInputRef}
                  style={styles.authInput}
                  placeholder="Password"
                  value={password}
                  onChangeText={setPassword}
                  editable={!loading}
                  secureTextEntry
                  returnKeyType="go"
                  onSubmitEditing={handleSubmit}
                  onKeyPress={(e) => {
                    if (Platform.OS === 'web' && e.nativeEvent.key === 'Enter') {
                      handleSubmit();
                    }
                  }}
                />
                {!isRegister && (
                  <TouchableOpacity
                    onPress={() => {
                      setIsForgotPassword(true);
                      setError(null);
                    }}
                    disabled={loading}
                  >
                    <Text style={[styles.authToggle, { textAlign: 'right', marginTop: 0, marginBottom: 8 }]}>
                      Forgot password?
                    </Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity
                  style={[
                    styles.button,
                    styles.primaryButton,
                    loading && styles.removeBtnDisabled,
                  ]}
                  onPress={handleSubmit}
                  disabled={loading}
                >
                  <Text style={styles.primaryButtonText}>
                    {loading
                      ? 'Processing…'
                      : isRegister
                        ? 'Register'
                        : 'Sign In'}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => setIsRegister(!isRegister)}
                  disabled={loading}
                >
                  <Text style={styles.authToggle}>
                    {isRegister
                      ? 'Have an account? Sign In'
                      : "Don't have an account? Register"}
                  </Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        </View>
      </View>
    </View>
  );
}
